import type {ScopedField} from '@github-ui/issue-create/OptionConfig'
import {type IssueFieldType, KnownTypes} from '@github-ui/issue-fields-shared/issue-field'
import invariant from 'tiny-invariant'

import {
  MemexColumnDataType,
  type MemexColumnDataType as MemexColumnDataTypeType,
} from '../api/columns/contracts/memex-column'
import type {PersistedOption} from '../api/columns/contracts/single-select'
import type {UpdateColumnValueAction} from '../api/columns/contracts/storage'
import type {ColumnModel} from '../models/column-model'
import {isAnySingleSelectColumnModel} from '../models/column-model/guards'

const dataTypeToIssueFieldType: Partial<Record<MemexColumnDataTypeType, IssueFieldType>> = {
  [MemexColumnDataType.Text]: KnownTypes.text.type,
  [MemexColumnDataType.SingleSelect]: KnownTypes.single_select.type,
  [MemexColumnDataType.Date]: KnownTypes.date.type,
  [MemexColumnDataType.Number]: KnownTypes.number.type,
}

type IssueFieldUpdateAction = UpdateColumnValueAction & {
  memexProjectColumnId: number | string
  isIssueField: true
}

const isIssueFieldUpdateAction = (action: UpdateColumnValueAction): action is IssueFieldUpdateAction => {
  return 'isIssueField' in action && action.isIssueField
}

const memexColumnToScopedField = (column: ColumnModel, action: IssueFieldUpdateAction): ScopedField | null => {
  if (!column.issueFieldId) return null
  if (!action.value) return null

  const fieldType = dataTypeToIssueFieldType[column.dataType]
  if (!fieldType) return null

  const baseScopedField = {
    fieldId: String(column.issueFieldId),
    fieldName: column.name,
    fieldType,
  }

  switch (action.dataType) {
    case MemexColumnDataType.Text:
      return {
        ...baseScopedField,
        value: action.value,
      }
    case MemexColumnDataType.Number:
      return {
        ...baseScopedField,
        value: action.value.value,
      }
    case MemexColumnDataType.Date:
      return {
        ...baseScopedField,
        value: action.value.value,
      }
    case MemexColumnDataType.SingleSelect: {
      const selectedOptionId = action.value.id
      invariant(isAnySingleSelectColumnModel(column), 'Column must be single select type')
      const option = column.settings.options.find((opt: PersistedOption) => opt.id === selectedOptionId)
      if (option) {
        return {
          ...baseScopedField,
          value: {
            id: option.id,
            name: option.name,
            description: option.description,
            color: option.color,
          },
        }
      }
      return null
    }
    default:
      return null
  }
}

export const buildScopedFieldsFromActions = (
  columns: ReadonlyArray<ColumnModel>,
  actions: Array<UpdateColumnValueAction> | undefined,
): Array<ScopedField> => {
  if (!actions?.length) return []

  const columnsById = new Map<string, ColumnModel>()
  for (const column of columns) {
    columnsById.set(String(column.id), column)
  }

  const scopedFields = new Map<string, ScopedField>()

  for (const action of actions) {
    if (!isIssueFieldUpdateAction(action)) continue

    const column = columnsById.get(String(action.memexProjectColumnId))
    if (!column) continue

    const scopedField = memexColumnToScopedField(column, action)
    if (scopedField) {
      scopedFields.set(scopedField.fieldId, scopedField)
    }
  }

  return Array.from(scopedFields.values())
}
